import { useState, useEffect, useRef } from 'react';
import '../styles/BillingSetup.css';
import { ChevronDown, EllipsisVertical, X } from 'lucide-react';
import { DataTable, MiniNav, NumberInput, Subheader } from './FieldComponents';
import Modal from './Modal';
import { BillingSetupInvoices } from './BillingSetupInvoices';
import { BillingSetupPayments as BillingPayments } from './BillingSetupPayments';
import { useNavVariant } from '../App';
import { getContactInitialsClassName } from '../utils/colorUtils';

export const BillingSetup = () => {
    const [rateSchedule, setRateSchedule] = useState(0);
    const [editRateSchedule, setEditRateSchedule] = useState(false);
    const [newRateSchedule, setNewRateSchedule] = useState(null);
    const [rateData, setRateData] = useState({});
    const navVariant = useNavVariant();
    const [expenseData, setExpenseData] = useState({});
    const [rateSchedules, setRateSchedules] = useState([]);
    const [timeIncrement, setTimeIncrement] = useState(0);
    const [billingNav, setBillingNav] = useState(0);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [users, setUsers] = useState([]);
    const [selectedMember, setSelectedMember] = useState('');
    const [editRate, setEditRate] = useState(null);
    const [teamMemberData, setTeamMemberData] = useState({});
    const [tkClassifications, setTkClassifications] = useState([]);
    const [codes, setCodes] = useState({ flat_fee_code: [], flat_fee_code_2: [] });
    const [selectedCodeSet, setSelectedCodeSet] = useState(0);
    const [newCode, setNewCode] = useState({ key: 'flat_fee_code', code: '', description: '' });
    const optionRef = useRef(null);
    
    const fetchBillingRates = async () => {
        const response = await fetch('https://api.casedb.co/billing.php');
        const data = await response.json();

        if (data.success) {
            const schedules = data.billing_rates || [];
            setRateSchedules(schedules);
            setTkClassifications(data.classifications || []);

            const defaultIndex = schedules.findIndex((rate) => Number(rate?.def) === 1);
            const nextIndex = defaultIndex !== -1 ? defaultIndex : rateSchedule;
            setRateSchedule(nextIndex);

            const selected = schedules[nextIndex];
            setTimeIncrement(selected?.time_increment ?? 0);
        }
    };

    const createBillingRate = async () => {    
        if (!newRateSchedule?.name) {
            return;
        }

        const templateIndex = newRateSchedule.template;
        const templateId = templateIndex !== '' && templateIndex !== null && templateIndex !== undefined
            ? rateSchedules[Number(templateIndex)]?.id
            : null;

        const response = await fetch('https://api.casedb.co/billing.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name: newRateSchedule['name'],
                def: newRateSchedule['def'],
                template: templateId
            })
        });
    
        const data = await response.json();
    
        if (data.success) {
            fetchBillingRates();
            setNewRateSchedule(null);
        } else {
            console.error('Failed to create billing rate:', data.message || data.error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/user.php?billing_rate=${rateSchedule + 1}&time=${new Date().getTime()}`, {
                credentials: 'include'
            });
            const data = await response.json();
            if (response.ok) {
                setUsers(data.users);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    }

    const updateRate = async () => {
        try {
            const resolvedClassification = teamMemberData.classification !== undefined
                ? teamMemberData.classification
                : editRate.classification_id;
            const resolvedRate = teamMemberData.rate !== undefined
                ? teamMemberData.rate
                : editRate.rate;
            const resolvedTimekeeper = teamMemberData.timekeeper_id !== undefined
                ? teamMemberData.timekeeper_id
                : editRate.timekeeper_id;

            const classificationValue = resolvedClassification === '' || resolvedClassification === null || resolvedClassification === undefined
                ? null
                : Number(resolvedClassification);
            const rateValue = resolvedRate === '' || resolvedRate === null || resolvedRate === undefined
                ? null
                : Number(resolvedRate);
            const timekeeperValue = resolvedTimekeeper === '' || resolvedTimekeeper === null || resolvedTimekeeper === undefined
                ? null
                : resolvedTimekeeper;

            const noneClassificationId = tkClassifications.find((item) => item.classification === '(None)')?.id;
            const normalizedClassificationValue = classificationValue === null && noneClassificationId
                ? Number(noneClassificationId)
                : classificationValue;

            const response = await fetch('https://api.casedb.co/billing.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: Number(editRate.id),
                    billing_rates_id: rateSchedule + 1,
                    classification_id: normalizedClassificationValue,
                    rate: rateValue,
                    timekeeper_id: timekeeperValue
                })
            });
            const data = await response.json();
            fetchUsers();
            setEditRate(null);
            setTeamMemberData({});
            
        } catch (err) {
            console.error(err);
        }
    }

    const fetchIncrement = async () => {
        try {
            const response = await fetch('https://api.casedb.co/billing.php');
            const data = await response.json();
            if (data.success) {
                setTimeIncrement(data.billing_rates[rateSchedule].time_increment ?? 0);
            }
        } catch (error) {
            console.error("Error fetching increment:", error);
        }
    }

    const updateIncrement = async (val) => {
        try {
            const response = await fetch('https://api.casedb.co/billing.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    billing_rates_id: rateSchedule + 1,
                    time_increment: Number(val)
                })
            });
            if (response.ok) {
                setTimeIncrement(Number(val) ?? 0);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        document.title = "Billing Setup | CaseDB";
        
        fetchBillingRates();
        fetchBillingCodes();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                optionRef.current &&
                !optionRef.current.contains(event.target)
            ) {
                setEditRateSchedule(false);
            }
        };
    
        if (editRateSchedule) {
            document.addEventListener("mousedown", handleClickOutside);
        }
    
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [editRateSchedule]);

    useEffect(() => {
        fetchUsers();
        fetchIncrement();
    }, [rateSchedule]);

    const fetchBillingCodes = async () => {
        try {
            const res = await fetch('https://api.casedb.co/billing_codes.php?time=' + Date.now());
            const data = await res.json();
            if (data.success) {
                setCodes({
                    flat_fee_code: data.flat_fee_code || [],
                    flat_fee_code_2: data.flat_fee_code_2 || []
                });
            }
        } catch (err) {
            console.error('Failed to fetch billing codes', err);
        }
    };

    const createBillingCode = async () => {
        try {
            const res = await fetch('https://api.casedb.co/billing_codes.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newCode)
            });
            const data = await res.json();
            if (data.success) {
                setNewCode({ key: newCode.key, code: '', description: '' });
                fetchBillingCodes();
            }
        } catch (err) { console.error(err); }
    };

    const deleteBillingCode = async (key, code) => {
        try {
            const res = await fetch(`https://api.casedb.co/billing_codes.php?key=${key}&code=${encodeURIComponent(code)}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) fetchBillingCodes();
        } catch (err) { console.error(err); }
    };

    // local delete helper used by this component
    const _deleteBillingCode = deleteBillingCode;

    return (
        <div className={`firm-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id='page-header' className='nm' style={{ justifyContent: 'space-between' }}>
                <h2 >Billing Setup</h2>
            </div>
            <div id='template-header'>
                {screenWidth < 729 ? (
                    <div className='filter-group'>
                        <div className='select-wrapper'>
                            <select className='filter-select' value={billingNav} onChange={(e) => setBillingNav(Number(e.target.value))}>
                                <option value={0}>Rates</option>
                                <option value={1}>Invoices</option>
                                <option value={2}>Payments</option>
                                <option value={3}>Codes</option>
                                <option value={4}>Email</option>
                                <option value={5}>Access</option>
                            </select>
                            <ChevronDown size={16} className='select-chevron'/>
                        </div>
                    </div>
                )  : (
                    <>
                        <h4 onClick={() => setBillingNav(0)} className={`${billingNav !== 0 ? 'in' : ''}active`}>Rates</h4>
                        <h4 onClick={() => setBillingNav(1)} className={`${billingNav !== 1 ? 'in' : ''}active`}>Invoices</h4>
                        <h4 onClick={() => setBillingNav(2)} className={`${billingNav !== 2 ? 'in' : ''}active`}>Payments</h4>
                        <h4 onClick={() => setBillingNav(3)} className={`${billingNav !== 3 ? 'in' : ''}active`}>Codes</h4>
                        <h4 onClick={() => setBillingNav(4)} className={`${billingNav !== 4 ? 'in' : ''}active`}>Email</h4>
                        <h4 onClick={() => setBillingNav(5)} className={`${billingNav !== 5 ? 'in' : ''}active`}>Access</h4>
                    </>
                )}
            </div>
            {billingNav === 0 ? (
                <div className='billing rates'>
                    <div className='rate-header'>
                        <div className='rate-schedule-selection'>
                            <div className='filter-group'>
                                <div className='select-wrapper'>
                                    <select 
                                        className='filter-select' 
                                        value={rateSchedule} 
                                        onChange={(e) => setRateSchedule(Number(e.target.value))}
                                    >
                                        {rateSchedules.map((rate, index) => (
                                            <option key={index} value={index}>{rate.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className='select-chevron'/>
                                </div>
                            </div>
                            <button 
                                className='form-box alt' 
                                onClick={() => setNewRateSchedule((prev) => prev ? null : { name: '', def: 0, template: '' })}
                            >
                                <X size={18} style={!newRateSchedule ? {transform: 'rotate(-45deg)'} : {}}/>
                            </button>
                            {newRateSchedule && 
                                <div className='new-rate-schedule'>
                                    <h4>Add a New Rate Schedule</h4>
                                    <div className='form-group'>
                                        <label className='subtext'>Name the New Rate Schedule</label>
                                        <input type='text' placeholder='Rate Schedule Name' value={newRateSchedule.name ?? ''} onChange={(e) => setNewRateSchedule({...newRateSchedule, name: e.target.value})}/>
                                    </div>
                                    <div className='filter-group'>
                                        <label className='subtext'>Create rate schedule based off of...</label>
                                        <div className='select-wrapper'>
                                            <select className='filter-select' value={newRateSchedule.template ?? ''} onChange={(e) => setNewRateSchedule({...newRateSchedule, template: e.target.value})}>
                                                <option value="">New blank rate schedule</option>
                                                {rateSchedules.map((rate, index) => (
                                                    <option key={index} value={index}>{rate.name}</option>
                                                ))}
                                            </select>
                                            <ChevronDown size={16} className='select-chevron'/>
                                        </div>
                                    </div>
                                    <div className='form-group alt'>
                                        <label className='subtext'>Default billing rates?</label>
                                        <input type='checkbox' hidden/>
                                        <div 
                                            className='checkbox'
                                            onClick={() => setNewRateSchedule((prev) => ({
                                                ...prev,
                                                def: prev?.def === 1 ? 0 : 1
                                            }))}
                                        >
                                            {newRateSchedule.def === 1 ? '✓' : ''}
                                        </div>
                                    </div>
                                    <div className='create-actions'>
                                        <button className='action' onClick={createBillingRate}>Create</button>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                    <div className='rate-schedule'>
                        <div className='title'>
                            <h4>{rateSchedules[rateSchedule]?.name}</h4>
                            {Number(rateSchedules[rateSchedule]?.def) === 1 
                                ? <span className='tag caps'>Default</span>
                                : ''
                            }
                            <div className='rate-actions' ref={optionRef}>
                                <div className={`rate-action ${editRateSchedule ? 'expanded' : ''}`} onClick={() => setEditRateSchedule((prev) => !prev)}>
                                    {!editRateSchedule ? <EllipsisVertical /> : <X/>}
                                </div>
                                {editRateSchedule && 
                                    <div className='rate-action-options'>
                                        <div className='rate-action-option'>Rename</div>
                                        <div className='rate-action-option'>Set as Default</div>
                                        <div className='rate-action-option'>Remove</div>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className='filter-group'>
                            <label className='subtext alt'>Minimum Time Increment</label>
                            <div className='select-wrapper'>
                                <select className='filter-select' value={timeIncrement} 
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setTimeIncrement(val);
                                        updateIncrement(val);
                                    }}
                                >
                                    <option value={0}>None</option>
                                    <option value={1}>0.1 Hour</option>
                                    <option value={2}>0.25 Hour</option>
                                </select>
                                <ChevronDown size={16} className='select-chevron'/>
                            </div>
                        </div>
                        <div className='instructions rate'>
                            Setting a minumum time increment will cause all entries for the billing timer and the time entry to be rounded up to the next closest time increment.
                        </div>
                        <div className='time-table'>
                            <div className='form-group full nm'>
                                <input
                                    type='text'
                                    className='default-input'
                                    placeholder='Search for a team member...'
                                    value={selectedMember}
                                    onChange={(e) => setSelectedMember(e.target.value)}
                                />
                            </div>
                            <table className='rate-table default'>
                                <thead>
                                    <tr className='default'>
                                        <th>Team Member</th>
                                        <th>Timekeeper ID</th>
                                        <th>Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .filter(user => {
                                            if (!selectedMember) return true;
                                            const term = selectedMember.toLowerCase();
                                            return (
                                                user.name?.toLowerCase().includes(term)
                                                || String(user.timekeeper_id ?? '').toLowerCase().includes(term)
                                            );
                                        })
                                        .map((user, index) => (
                                        <tr key={user.id} onClick={() => setEditRate(user)} className={`default ${index % 2 !== 0 && 'odd-rate-row'}`}>
                                            <td>
                                                <span className={`${getContactInitialsClassName(user.id)}`}>
                                                    {user.profile_picture 
                                                        ? (
                                                            <img src={`https://api.casedb.co/${user.profile_picture}`}/> 
                                                        ) : (
                                                            (() => {
                                                                const parts = user.name?.trim().split(' ').filter(Boolean) || [];
                                                                const first = parts?.[0]?.[0] || '';
                                                                const last = parts?.[parts.length - 1]?.[0] || '';
                                                                return (first + last).toUpperCase();
                                                            })()
                                                        )}
                                                        </span>
                                                {user.name}
                                            </td>
                                            <td>{user.timekeeper_id}</td>
                                            <td style={{ color: user?.rate ? 'var(--fill)' : '' }}>{user.rate}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {editRate &&
                                <Modal
                                    onClose={() => setEditRate(null)}
                                    title="Edit User Billing Rate"
                                    header={(
                                        <div className='modal-header-actions'>
                                            <button className='action alt' onClick={() => setEditRate(null)}>Cancel</button>
                                            <button className='action' onClick={() => updateRate()}>Save</button>
                                        </div>
                                    )}
                                >
                                     <div className='modal-content-wrapper'>
                                        <div className='user-name'>
                                        <span className={`${getContactInitialsClassName(editRate.id)}`}>
                                                    {editRate.profile_picture 
                                                    ? (
                                                        <img src={`https://api.casedb.co/${editRate.profile_picture}`}/> 
                                                    ) : (
                                                        (() => {
                                                            const parts = editRate.name?.trim().split(' ');
                                                            const first = parts?.[0]?.[0] || '';
                                                            const last = parts?.[parts.length - 1]?.[0] || '';
                                                            return (first + last).toUpperCase();
                                                        })()
                                                    )}
                                                </span>
                                            {editRate.name}
                                        </div>
                                        <div className='form-group'>
                                            <label className='subtext'>Timekeeper ID</label>
                                            <input
                                                type='text'
                                                placeholder='Timekeeper ID'
                                                value={teamMemberData.timekeeper_id !== undefined ? teamMemberData.timekeeper_id : (editRate.timekeeper_id ?? '')}
                                                onChange={(e) => setTeamMemberData({ ...teamMemberData, timekeeper_id: e.target.value})}
                                            />
                                        </div>
                                        <div className='form-group'>
                                            <label className='subtext'>Rate</label>
                                            <NumberInput type='currency' value={teamMemberData.rate !== undefined ? teamMemberData.rate : editRate.rate} onChange={(val) => setTeamMemberData({ ...teamMemberData, rate: val})} />
                                        </div>
                                        <div className='form-group'>
                                            <label className='subtext'>Timekeeper Classification</label>
                                            {(() => {
                                                const noneClassificationId = tkClassifications.find((item) => item.classification === '(None)')?.id;
                                                const selectedClassification = teamMemberData.classification !== undefined
                                                    ? teamMemberData.classification
                                                    : (editRate.classification_id ?? '');
                                                const resolvedValue = (selectedClassification === '' || selectedClassification === null || selectedClassification === undefined)
                                                    ? (noneClassificationId ?? '')
                                                    : selectedClassification;
                                                return (
                                                    <div className='select-wrapper'>
                                                        <select className='default-select' value={resolvedValue} onChange={(e) => setTeamMemberData({ ...teamMemberData, classification: e.target.value})}>
                                                            {(() => {
                                                                const selectedRateId = rateSchedules[rateSchedule]?.id;
                                                                const settingsId = rateSchedules[rateSchedule]?.settings;
                                                                const matching = tkClassifications.filter((classification) =>
                                                                    Number(classification.rate_id) === Number(selectedRateId)
                                                                    || Number(classification.rate_id) === Number(settingsId)
                                                                );
                                                                const list = matching.length > 0 ? matching : tkClassifications;
                                                                return list.map((classification) => (
                                                                <option key={classification.id} value={classification.id}>{classification.classification}</option>
                                                                ));
                                                            })()}
                                                        </select>
                                                        <ChevronDown size={16} className='select-chevron'/>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                     </div>
                                </Modal>
                            }
                        </div>
                    </div>
                </div>
            ) : billingNav === 1 ? (
                <BillingSetupInvoices />
            ) : billingNav === 2 ? (
                <BillingPayments />
            ) : billingNav === 3 ? (
                <div className='billing codes'>
                    <div className='codes-header'>
                        <h2 style={{ marginTop: 0 }}>Codesets</h2>
                        <MiniNav options={['FlatFeeCode', 'FlatFeeCode2']} selectedOption={selectedCodeSet} setSelectedOption={(val) => setSelectedCodeSet(val)} />
                    </div>
                    <div className='codes-columns'>
                        {selectedCodeSet === 0 && <div className='codes-column'>
                            <h4 style={{ marginTop: 0 }}>FlatFeeCode</h4>
                            <table className='codes-list'>
                                <thead>
                                    <tr>
                                        <th className='code-label'>Code</th>
                                        <th className='code-desc'>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {codes.flat_fee_code.map((c, idx) => (
                                        <tr key={idx} className='code-row'>
                                            <td className='code-label subtext'>{c.code}</td>
                                            <td className='code-desc subtext'>
                                                {c.description}
                                                <div className='new-button destroy' onClick={() => deleteBillingCode('flat_fee_code', c.code)}>
                                                    remove
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className='code-add form-group middle'>
                                <input style={{ maxWidth: "200px" }} placeholder='Enter code' value={newCode.key === 'flat_fee_code' ? newCode.code : ''} onChange={(e) => setNewCode({ ...newCode, key: 'flat_fee_code', code: e.target.value })} />
                                <input placeholder='Enter code description' value={newCode.key === 'flat_fee_code' ? newCode.description : ''} onChange={(e) => setNewCode({ ...newCode, key: 'flat_fee_code', description: e.target.value })} />
                                <div className='form-box alt' onClick={createBillingCode}>+</div>
                            </div>
                        </div>}

                        {selectedCodeSet === 1 && <div className='codes-column'>
                            <h4 style={{ marginTop: 0 }}>FlatFeeCode2</h4>
                            <table className='codes-list'>
                                <thead>
                                    <tr>
                                        <th className='code-label'>Code</th>
                                        <th className='code-desc'>Description</th>
                                    </tr>
                                </thead>
                                {codes?.flat_fee_code_2.length > 0 ? (
                                    <tbody>
                                        {codes.flat_fee_code_2.map((c, idx) => (
                                            <tr key={idx} className='code-row'>
                                                <td className='code-label subtext'>{c.code}</td>
                                                <td className='code-desc subtext'>{c.description}</td>
                                                <td>
                                                    <div className='new-button destroy' onClick={() => deleteBillingCode('flat_fee_code_2', c.code)}>remove</div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                ) : (
                                    <tbody style={{ width: '100%' }}>
                                        <tr>
                                            <td style={{ colSpan: '2' }} className='subtext'>No codes found.</td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                            <div className='code-add form-group middle'>
                                <input style={{ maxWidth: "200px" }} placeholder='Enter code' value={newCode.key === 'flat_fee_code_2' ? newCode.code : ''} onChange={(e) => setNewCode({ ...newCode, key: 'flat_fee_code_2', code: e.target.value })} />
                                <input placeholder='Enter code description' value={newCode.key === 'flat_fee_code_2' ? newCode.description : ''} onChange={(e) => setNewCode({ ...newCode, key: 'flat_fee_code_2', description: e.target.value })} />
                                <div className='form-box alt' onClick={createBillingCode}>+</div>
                            </div>
                        </div>}
                    </div>
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}

export const removeBillingCode = async (key, code) => {
    try {
        const res = await fetch(`https://api.casedb.co/billing_codes.php?key=${key}&code=${encodeURIComponent(code)}`, { method: 'DELETE' });
        const data = await res.json();
        return !!data.success;
    } catch (err) {
        console.error('removeBillingCode error', err);
        return false;
    }
};